import { ApiRequestError } from '@/lib/httpClient'
import type { AdvicePrepPaywallReason } from '../types'
import { isAdvicePrepPaywallReason } from './constants'

type PaymentRequiredBody = {
  paywalled?: boolean
  reason?: string
}

/**
 * The API answers a paywalled endpoint with 402 rather than 401 or 403: 401 is
 * consumed by the token-refresh path in httpClient and would retry forever, and
 * 403 is indistinguishable from a genuine permission denial.
 */
export function advicePrepPaymentRequiredReason(error: unknown): AdvicePrepPaywallReason | null {
  if (!(error instanceof ApiRequestError) || error.status !== 402) return null
  const body = error.payload as PaymentRequiredBody | null | undefined
  if (body?.paywalled !== true) return null
  return isAdvicePrepPaywallReason(body.reason) ? body.reason : null
}

// The API caches the paywall decision per instance, so evicting it on payment
// only clears the instance that took the payment. For a short window afterwards
// another instance can still answer 402 to a customer who has genuinely paid,
// and one retry is the difference between riding that out and telling them they
// have not paid.
const PAYMENT_SETTLE_WINDOW_MS = 30_000
export const ADVICEPREP_PAYMENT_RETRY_DELAY_MS = 1500

let lastPaymentAt = 0

export function markAdvicePrepPaymentSucceeded() {
  lastPaymentAt = Date.now()
}

export function isWithinAdvicePrepPaymentWindow() {
  return lastPaymentAt > 0 && Date.now() - lastPaymentAt < PAYMENT_SETTLE_WINDOW_MS
}

type PaymentRequiredHandler = (reason: AdvicePrepPaywallReason) => void

let handler: PaymentRequiredHandler | null = null

export function onAdvicePrepPaymentRequired(next: PaymentRequiredHandler | null) {
  handler = next
}

export function reportAdvicePrepPaymentRequired(reason: AdvicePrepPaywallReason) {
  handler?.(reason)
}
